import type React from 'react';
import { useState, useEffect, useRef } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import {
  Logo,
  FolderIcon,
  SettingsIcon,
  LogOutIcon,
  SunIcon,
  MoonIcon,
  Tooltip,
  Button,
  useTheme,
} from '@shadeables/ui';
import styles from './LoggedInLayout.module.css';

const LoggedInLayout: React.FC = () => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  const toggleSettings = () => {
    setIsSettingsOpen(!isSettingsOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        settingsRef.current &&
        !settingsRef.current.contains(event.target as Node)
      ) {
        setIsSettingsOpen(false);
      }
    };

    if (isSettingsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSettingsOpen]);
  return (
    <div className={styles.layout}>
      <aside className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <Tooltip content="Dashboard">
            <Link to="/dashboard" className={styles.logoContainer}>
              <Logo size="small" showText={false} />
            </Link>
          </Tooltip>
          <nav>
            <Tooltip content="Projects">
              <Link to="/dashboard" className={styles.iconLink}>
                <FolderIcon size={20} />
              </Link>
            </Tooltip>
          </nav>
        </div>
        <div className={styles.sidebarFooter}>
          <div className={styles.settingsContainer} ref={settingsRef}>
            <Tooltip content="Settings">
              <Button
                variant="icon"
                onClick={toggleSettings}
                className={`${styles.iconLink} ${
                  isSettingsOpen ? styles.active : ''
                }`}
              >
                <SettingsIcon size={20} />
              </Button>
            </Tooltip>
            {isSettingsOpen && (
              <div className={styles.submenu}>
                <Button
                  variant="ghost"
                  size="sm"
                  className={styles.submenuItem}
                  onClick={toggleTheme}
                  leftIcon={
                    theme === 'light' ? (
                      <SunIcon size={16} />
                    ) : (
                      <MoonIcon size={16} />
                    )
                  }
                >
                  Toggle Theme
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className={styles.submenuItem}
                  onClick={() => {
                    setIsSettingsOpen(false);
                    navigate('/settings/user');
                  }}
                  leftIcon={<SettingsIcon size={16} />}
                >
                  User Settings
                </Button>
              </div>
            )}
          </div>
          <Tooltip content="Logout">
            <Link to="/logout" className={styles.iconLink}>
              <LogOutIcon size={20} />
            </Link>
          </Tooltip>
        </div>
      </aside>
      <main className={styles.main}>
        <Outlet />
      </main>
    </div>
  );
};

export default LoggedInLayout;
